// Initialize Common HAI Diagnoses
// Run this once to populate common healthcare-associated infection diagnoses with ICD-10 codes
// Based on CDC/NHSN definitions and common clinical practice

import diagnosisService from '../services/diagnosisService';

/**
 * Common HAI Diagnoses with ICD-10 codes
 * Based on CDC/NHSN definitions and clinical practice
 */
const commonHAIDiagnoses = [
  // ===== CAUTI (Catheter-Associated Urinary Tract Infection) =====
  {
    icd10Code: 'N39.0',
    icd10Description: 'Urinary tract infection, site not specified',
    commonName: 'Urinary Tract Infection',
    category: 'Urinary Tract Infection',
    subCategory: 'Catheter-Associated',
    ipcRelevant: true,
    haiType: 'CAUTI',
    isHAI: true,
    riskMultiplier: 1.5,
    nhsnReportable: true,
    cdcNotifiable: true,
    euNotifiable: false,
    typicalSeverity: 'Moderate',
    affectedSystems: ['Urinary'],
    clinicalPresentation: {
      symptoms: ['Dysuria', 'Frequency', 'Urgency', 'Suprapubic pain', 'Fever'],
      typicalCourse: 'Acute onset, typically resolves with treatment in 3-7 days',
      diagnosticCriteria: ['Positive urine culture', 'Symptoms', 'Catheter in place']
    },
    isActive: true,
    displayOrder: 1
  },
  {
    icd10Code: 'N39.0',
    icd10Description: 'Urinary tract infection, site not specified',
    commonName: 'Catheter-Associated UTI',
    category: 'Urinary Tract Infection',
    subCategory: 'Catheter-Associated',
    ipcRelevant: true,
    haiType: 'CAUTI',
    isHAI: true,
    riskMultiplier: 1.8,
    nhsnReportable: true,
    cdcNotifiable: true,
    euNotifiable: false,
    typicalSeverity: 'Moderate',
    affectedSystems: ['Urinary'],
    clinicalPresentation: {
      symptoms: ['Catheter-related symptoms', 'Fever', 'Suprapubic tenderness'],
      typicalCourse: 'Healthcare-associated infection, requires catheter removal',
      diagnosticCriteria: ['Catheter in place ≥2 days', 'Positive culture', 'Symptoms']
    },
    isActive: true,
    displayOrder: 2
  },

  // ===== CLABSI (Central Line-Associated Bloodstream Infection) =====
  {
    icd10Code: 'A41.9',
    icd10Description: 'Sepsis, unspecified organism',
    commonName: 'Bloodstream Infection',
    category: 'Bloodstream Infection',
    subCategory: 'Central Line-Associated',
    ipcRelevant: true,
    haiType: 'CLABSI',
    isHAI: true,
    riskMultiplier: 2.0,
    nhsnReportable: true,
    cdcNotifiable: true,
    euNotifiable: false,
    typicalSeverity: 'Severe',
    affectedSystems: ['Cardiovascular', 'Immune'],
    clinicalPresentation: {
      symptoms: ['Fever', 'Chills', 'Hypotension', 'Tachycardia', 'Altered mental status'],
      typicalCourse: 'Acute onset, can progress to septic shock if untreated',
      diagnosticCriteria: ['Positive blood culture', 'Central line in place', 'No other source']
    },
    isActive: true,
    displayOrder: 3
  },
  {
    icd10Code: 'T80.211A',
    icd10Description: 'Bloodstream infection due to central venous catheter, initial encounter',
    commonName: 'Central Line-Associated BSI',
    category: 'Bloodstream Infection',
    subCategory: 'Central Line-Associated',
    ipcRelevant: true,
    haiType: 'CLABSI',
    isHAI: true,
    riskMultiplier: 2.2,
    nhsnReportable: true,
    cdcNotifiable: true,
    euNotifiable: false,
    typicalSeverity: 'Severe',
    affectedSystems: ['Cardiovascular', 'Immune'],
    clinicalPresentation: {
      symptoms: ['Fever', 'Chills', 'Hypotension', 'Catheter site inflammation'],
      typicalCourse: 'Requires immediate central line removal and antibiotic therapy',
      diagnosticCriteria: ['Central line in place ≥2 days', 'Positive blood culture', 'Clinical signs']
    },
    isActive: true,
    displayOrder: 4
  },

  // ===== VAP (Ventilator-Associated Pneumonia) =====
  {
    icd10Code: 'J18.9',
    icd10Description: 'Pneumonia, unspecified organism',
    commonName: 'Pneumonia',
    category: 'Respiratory Infection',
    subCategory: 'Ventilator-Associated',
    ipcRelevant: true,
    haiType: 'VAP',
    isHAI: true,
    riskMultiplier: 2.0,
    nhsnReportable: true,
    cdcNotifiable: true,
    euNotifiable: false,
    typicalSeverity: 'Severe',
    affectedSystems: ['Respiratory'],
    clinicalPresentation: {
      symptoms: ['Fever', 'Purulent sputum', 'Increased oxygen requirements', 'New infiltrate on CXR'],
      typicalCourse: 'Develops ≥48 hours after intubation, requires ventilator support',
      diagnosticCriteria: ['Ventilator in place ≥2 days', 'New infiltrate', 'Clinical signs']
    },
    isActive: true,
    displayOrder: 5
  },
  {
    icd10Code: 'J95.851',
    icd10Description: 'Ventilator associated pneumonia',
    commonName: 'Ventilator-Associated Pneumonia',
    category: 'Respiratory Infection',
    subCategory: 'Ventilator-Associated',
    ipcRelevant: true,
    haiType: 'VAP',
    isHAI: true,
    riskMultiplier: 2.2,
    nhsnReportable: true,
    cdcNotifiable: true,
    euNotifiable: false,
    typicalSeverity: 'Severe',
    affectedSystems: ['Respiratory'],
    clinicalPresentation: {
      symptoms: ['Fever', 'Purulent secretions', 'Worsening oxygenation', 'New infiltrate'],
      typicalCourse: 'High mortality, requires ventilator bundle compliance',
      diagnosticCriteria: ['Ventilator in place ≥2 days', 'New infiltrate on CXR', 'Clinical signs']
    },
    isActive: true,
    displayOrder: 6
  },

  // ===== SSI (Surgical Site Infection) =====
  {
    icd10Code: 'T81.4XXA',
    icd10Description: 'Infection following a procedure, initial encounter',
    commonName: 'Surgical Site Infection',
    category: 'Surgical Site Infection',
    subCategory: 'Healthcare-Associated',
    ipcRelevant: true,
    haiType: 'SSI',
    isHAI: true,
    riskMultiplier: 1.8,
    nhsnReportable: true,
    cdcNotifiable: true,
    euNotifiable: false,
    typicalSeverity: 'Moderate',
    affectedSystems: ['Skin', 'Soft Tissue'],
    clinicalPresentation: {
      symptoms: ['Erythema', 'Warmth', 'Swelling', 'Purulent drainage', 'Fever'],
      typicalCourse: 'Develops within 30 days (90 days for implants) of surgery',
      diagnosticCriteria: ['Surgery within time window', 'Infection at surgical site', 'Clinical signs']
    },
    isActive: true,
    displayOrder: 7
  },
  {
    icd10Code: 'T81.4XXD',
    icd10Description: 'Infection following a procedure, subsequent encounter',
    commonName: 'Surgical Site Infection (Subsequent)',
    category: 'Surgical Site Infection',
    subCategory: 'Healthcare-Associated',
    ipcRelevant: true,
    haiType: 'SSI',
    isHAI: true,
    riskMultiplier: 1.8,
    nhsnReportable: true,
    cdcNotifiable: true,
    euNotifiable: false,
    typicalSeverity: 'Moderate',
    affectedSystems: ['Skin', 'Soft Tissue'],
    clinicalPresentation: {
      symptoms: ['Erythema', 'Warmth', 'Swelling', 'Purulent drainage', 'Fever'],
      typicalCourse: 'Follow-up for SSI management',
      diagnosticCriteria: ['Previous surgery', 'Ongoing infection', 'Clinical signs']
    },
    isActive: true,
    displayOrder: 8
  },

  // ===== CDI (Clostridioides difficile Infection) =====
  {
    icd10Code: 'A04.7',
    icd10Description: 'Enterocolitis due to Clostridium difficile',
    commonName: 'C. difficile Infection',
    category: 'Gastrointestinal Infection',
    subCategory: 'Healthcare-Associated',
    ipcRelevant: true,
    haiType: 'CDI',
    isHAI: true,
    riskMultiplier: 1.8,
    nhsnReportable: true,
    cdcNotifiable: true,
    euNotifiable: false,
    typicalSeverity: 'Moderate',
    affectedSystems: ['Gastrointestinal'],
    clinicalPresentation: {
      symptoms: ['Diarrhea', 'Abdominal pain', 'Fever', 'Leukocytosis'],
      typicalCourse: 'Antibiotic-associated diarrhea, can recur',
      diagnosticCriteria: ['Positive toxin test', 'Diarrhea', 'Recent antibiotic use']
    },
    isActive: true,
    displayOrder: 9
  },
  {
    icd10Code: 'A04.72',
    icd10Description: 'Enterocolitis due to Clostridium difficile, recurrent',
    commonName: 'Recurrent C. difficile Infection',
    category: 'Gastrointestinal Infection',
    subCategory: 'Healthcare-Associated',
    ipcRelevant: true,
    haiType: 'CDI',
    isHAI: true,
    riskMultiplier: 2.0,
    nhsnReportable: true,
    cdcNotifiable: true,
    euNotifiable: false,
    typicalSeverity: 'Severe',
    affectedSystems: ['Gastrointestinal'],
    clinicalPresentation: {
      symptoms: ['Recurrent diarrhea', 'Abdominal pain', 'Fever', 'Dehydration'],
      typicalCourse: 'Recurrence within 8 weeks, may require fecal transplant',
      diagnosticCriteria: ['Previous CDI', 'Recurrent symptoms', 'Positive toxin test']
    },
    isActive: true,
    displayOrder: 10
  },

  // ===== Other Common HAI Diagnoses =====
  {
    icd10Code: 'J18.0',
    icd10Description: 'Bronchopneumonia, unspecified organism',
    commonName: 'Bronchopneumonia',
    category: 'Respiratory Infection',
    subCategory: 'Healthcare-Associated',
    ipcRelevant: true,
    haiType: 'Other HAI',
    isHAI: true,
    riskMultiplier: 1.6,
    nhsnReportable: false,
    cdcNotifiable: false,
    euNotifiable: false,
    typicalSeverity: 'Moderate',
    affectedSystems: ['Respiratory'],
    clinicalPresentation: {
      symptoms: ['Cough', 'Fever', 'Dyspnea', 'Chest pain'],
      typicalCourse: 'Lower respiratory tract infection',
      diagnosticCriteria: ['Clinical signs', 'Radiographic findings', 'Microbiologic evidence']
    },
    isActive: true,
    displayOrder: 11
  },
  {
    icd10Code: 'A41.51',
    icd10Description: 'Sepsis due to Escherichia coli',
    commonName: 'E. coli Sepsis',
    category: 'Bloodstream Infection',
    subCategory: 'Healthcare-Associated',
    ipcRelevant: true,
    haiType: 'Other HAI',
    isHAI: true,
    riskMultiplier: 1.8,
    nhsnReportable: false,
    cdcNotifiable: false,
    euNotifiable: false,
    typicalSeverity: 'Severe',
    affectedSystems: ['Cardiovascular', 'Immune'],
    clinicalPresentation: {
      symptoms: ['Fever', 'Chills', 'Hypotension', 'Tachycardia'],
      typicalCourse: 'Gram-negative sepsis, can progress rapidly',
      diagnosticCriteria: ['Positive blood culture', 'E. coli identified', 'Clinical signs']
    },
    isActive: true,
    displayOrder: 12
  },
  {
    icd10Code: 'A41.02',
    icd10Description: 'Sepsis due to Methicillin resistant Staphylococcus aureus',
    commonName: 'MRSA Sepsis',
    category: 'Bloodstream Infection',
    subCategory: 'Healthcare-Associated',
    ipcRelevant: true,
    haiType: 'Other HAI',
    isHAI: true,
    riskMultiplier: 2.0,
    nhsnReportable: false,
    cdcNotifiable: true,
    euNotifiable: false,
    typicalSeverity: 'Severe',
    affectedSystems: ['Cardiovascular', 'Immune'],
    clinicalPresentation: {
      symptoms: ['Fever', 'Chills', 'Hypotension', 'Tachycardia'],
      typicalCourse: 'MRSA bloodstream infection, requires vancomycin',
      diagnosticCriteria: ['Positive blood culture', 'MRSA identified', 'Clinical signs']
    },
    isActive: true,
    displayOrder: 13
  },
  {
    icd10Code: 'B96.21',
    icd10Description: 'Klebsiella pneumoniae as the cause of diseases classified elsewhere',
    commonName: 'Klebsiella pneumoniae Infection',
    category: 'Respiratory Infection',
    subCategory: 'Healthcare-Associated',
    ipcRelevant: true,
    haiType: 'Other HAI',
    isHAI: true,
    riskMultiplier: 1.8,
    nhsnReportable: false,
    cdcNotifiable: false,
    euNotifiable: false,
    typicalSeverity: 'Moderate',
    affectedSystems: ['Respiratory'],
    clinicalPresentation: {
      symptoms: ['Fever', 'Cough', 'Purulent sputum', 'Dyspnea'],
      typicalCourse: 'Common in ICU, ventilator-associated',
      diagnosticCriteria: ['Positive culture', 'K. pneumoniae identified', 'Clinical signs']
    },
    isActive: true,
    displayOrder: 14
  },
  {
    icd10Code: 'B96.5',
    icd10Description: 'Pseudomonas (aeruginosa) (mallei) (pseudomallei) as the cause of diseases classified elsewhere',
    commonName: 'Pseudomonas aeruginosa Infection',
    category: 'Respiratory Infection',
    subCategory: 'Healthcare-Associated',
    ipcRelevant: true,
    haiType: 'Other HAI',
    isHAI: true,
    riskMultiplier: 1.8,
    nhsnReportable: false,
    cdcNotifiable: false,
    euNotifiable: false,
    typicalSeverity: 'Moderate',
    affectedSystems: ['Respiratory', 'Urinary'],
    clinicalPresentation: {
      symptoms: ['Fever', 'Purulent secretions', 'Multidrug resistance'],
      typicalCourse: 'Common in ICU, difficult to treat',
      diagnosticCriteria: ['Positive culture', 'P. aeruginosa identified', 'Clinical signs']
    },
    isActive: true,
    displayOrder: 15
  },
  {
    icd10Code: 'B95.62',
    icd10Description: 'Methicillin resistant Staphylococcus aureus infection as the cause of diseases classified elsewhere',
    commonName: 'MRSA Infection',
    category: 'Skin/Soft Tissue Infection',
    subCategory: 'Healthcare-Associated',
    ipcRelevant: true,
    haiType: 'Other HAI',
    isHAI: true,
    riskMultiplier: 1.8,
    nhsnReportable: false,
    cdcNotifiable: true,
    euNotifiable: false,
    typicalSeverity: 'Moderate',
    affectedSystems: ['Skin', 'Soft Tissue'],
    clinicalPresentation: {
      symptoms: ['Erythema', 'Swelling', 'Purulent drainage', 'Fever'],
      typicalCourse: 'MRSA skin/soft tissue infection',
      diagnosticCriteria: ['Positive culture', 'MRSA identified', 'Clinical signs']
    },
    isActive: true,
    displayOrder: 16
  },
  {
    icd10Code: 'B95.61',
    icd10Description: 'Methicillin susceptible Staphylococcus aureus infection as the cause of diseases classified elsewhere',
    commonName: 'MSSA Infection',
    category: 'Skin/Soft Tissue Infection',
    subCategory: 'Healthcare-Associated',
    ipcRelevant: true,
    haiType: 'Other HAI',
    isHAI: true,
    riskMultiplier: 1.5,
    nhsnReportable: false,
    cdcNotifiable: false,
    euNotifiable: false,
    typicalSeverity: 'Mild',
    affectedSystems: ['Skin', 'Soft Tissue'],
    clinicalPresentation: {
      symptoms: ['Erythema', 'Swelling', 'Purulent drainage'],
      typicalCourse: 'MSSA skin/soft tissue infection',
      diagnosticCriteria: ['Positive culture', 'MSSA identified', 'Clinical signs']
    },
    isActive: true,
    displayOrder: 17
  },
  {
    icd10Code: 'B37.0',
    icd10Description: 'Candidal stomatitis',
    commonName: 'Candidiasis',
    category: 'Skin/Soft Tissue Infection',
    subCategory: 'Healthcare-Associated',
    ipcRelevant: true,
    haiType: 'Other HAI',
    isHAI: true,
    riskMultiplier: 1.5,
    nhsnReportable: false,
    cdcNotifiable: false,
    euNotifiable: false,
    typicalSeverity: 'Mild',
    affectedSystems: ['Skin', 'Mucous Membranes'],
    clinicalPresentation: {
      symptoms: ['White plaques', 'Erythema', 'Discomfort'],
      typicalCourse: 'Fungal infection, common in immunocompromised',
      diagnosticCriteria: ['Clinical appearance', 'Positive culture', 'Microscopy']
    },
    isActive: true,
    displayOrder: 18
  },
  {
    icd10Code: 'B37.2',
    icd10Description: 'Candidiasis of skin and nail',
    commonName: 'Cutaneous Candidiasis',
    category: 'Skin/Soft Tissue Infection',
    subCategory: 'Healthcare-Associated',
    ipcRelevant: true,
    haiType: 'Other HAI',
    isHAI: true,
    riskMultiplier: 1.5,
    nhsnReportable: false,
    cdcNotifiable: false,
    euNotifiable: false,
    typicalSeverity: 'Mild',
    affectedSystems: ['Skin'],
    clinicalPresentation: {
      symptoms: ['Erythema', 'Pruritus', 'Scaling'],
      typicalCourse: 'Fungal skin infection',
      diagnosticCriteria: ['Clinical appearance', 'Positive culture', 'Microscopy']
    },
    isActive: true,
    displayOrder: 19
  },
  {
    icd10Code: 'B37.81',
    icd10Description: 'Candidal endocarditis',
    commonName: 'Candidal Endocarditis',
    category: 'Bloodstream Infection',
    subCategory: 'Healthcare-Associated',
    ipcRelevant: true,
    haiType: 'Other HAI',
    isHAI: true,
    riskMultiplier: 2.0,
    nhsnReportable: false,
    cdcNotifiable: false,
    euNotifiable: false,
    typicalSeverity: 'Severe',
    affectedSystems: ['Cardiovascular'],
    clinicalPresentation: {
      symptoms: ['Fever', 'Heart murmur', 'Embolic events', 'Splenomegaly'],
      typicalCourse: 'Fungal endocarditis, high mortality',
      diagnosticCriteria: ['Positive blood culture', 'Echocardiographic findings', 'Clinical signs']
    },
    isActive: true,
    displayOrder: 20
  }
];

/**
 * Initialize common HAI diagnoses
 * @param {string} organizationId - Organization ID
 * @param {string} userId - User ID (default: 'system-init')
 * @param {string} userName - User name (default: 'System Initialization')
 */
async function initializeCommonHAIDiagnoses(organizationId, userId = 'system-init', userName = 'System Initialization') {
  console.log('🚀 Starting common HAI diagnoses initialization...');

  if (!organizationId) {
    console.error('❌ Organization ID is required');
    return {
      success: false,
      message: 'Organization ID is required'
    };
  }

  try {
    // Check if diagnoses already exist
    console.log('🔍 Checking for existing diagnoses...');
    const existingDiagnoses = await diagnosisService.getDiagnoses(organizationId, { activeOnly: false });
    
    if (existingDiagnoses.length > 0) {
      console.log(`⚠️  ${existingDiagnoses.length} diagnoses already exist. Skipping initialization.`);
      return {
        success: false,
        message: `Diagnoses already exist (${existingDiagnoses.length} found). Please use the UI to add diagnoses.`
      };
    }

    // Create diagnoses
    console.log(`📋 Creating ${commonHAIDiagnoses.length} common HAI diagnoses...`);
    const createdDiagnoses = [];

    for (const diagnosis of commonHAIDiagnoses) {
      try {
        const diagnosisData = {
          ...diagnosis,
          organizationId,
          causativePathogens: [],
          commonPathogenNames: [],
          increasesSusceptibility: [],
          linkedProtocols: [],
          linkedRisks: []
        };

        const diagnosisId = await diagnosisService.createDiagnosis(
          diagnosisData,
          userId,
          userName
        );

        createdDiagnoses.push({
          id: diagnosisId,
          icd10Code: diagnosis.icd10Code,
          commonName: diagnosis.commonName
        });

        console.log(`✅ Created: ${diagnosis.commonName} (${diagnosis.icd10Code})`);
      } catch (error) {
        console.error(`❌ Failed to create ${diagnosis.commonName}:`, error.message);
      }
    }

    console.log(`✅ Initialization complete! Created ${createdDiagnoses.length} diagnoses.`);
    
    return {
      success: true,
      message: `Successfully created ${createdDiagnoses.length} common HAI diagnoses`,
      created: createdDiagnoses
    };
  } catch (error) {
    console.error('❌ Error initializing common HAI diagnoses:', error);
    return {
      success: false,
      message: `Error: ${error.message}`
    };
  }
}

export default initializeCommonHAIDiagnoses;

// For Node.js script execution
if (typeof require !== 'undefined' && require.main === module) {
  const { initializeApp } = require('firebase/app');
  const { getFirestore } = require('firebase/firestore');
  
  // Firebase configuration
  const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyA5sIgAXtt1JxelqMp5KetVcgsmlCgmHok",
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "zero-path-c6391.firebaseapp.com",
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "zero-path-c6391",
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "zero-path-c6391.firebasestorage.app",
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "239371962993",
    appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:239371962993:web:d314b9afafd2135fb0e9d0"
  };

  const app = initializeApp(firebaseConfig);
  const db = getFirestore(app);

  // Get organization ID from command line or environment
  const organizationId = process.argv[2] || process.env.REACT_APP_ORGANIZATION_ID;

  if (!organizationId) {
    console.error('❌ Organization ID is required');
    console.error('Usage: node initialize-common-hai-diagnoses.js <organizationId>');
    process.exit(1);
  }

  initializeCommonHAIDiagnoses(organizationId)
    .then(result => {
      if (result.success) {
        console.log('✅', result.message);
        process.exit(0);
      } else {
        console.error('❌', result.message);
        process.exit(1);
      }
    })
    .catch(error => {
      console.error('❌ Fatal error:', error);
      process.exit(1);
    });
}

